import { useTheme } from '@emotion/react';
import dynamic from 'next/dynamic';
import React from 'react';

import { Box, Grid } from '~/components/blocks';
import { Encounter } from '~/graphql/generated/graphql';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';

import { PatientProfilePanel } from './PatientProfilePanel';
import { PermissionError } from './PermissionError';
import { useFetchEncounter } from './use-fetch-encounter';

const TelemedicinePanel = dynamic(() => import('./TelemedicinePanel'), {
  ssr: false,
});

const TelemedicinePanelV2 = dynamic(() => import('./TelemedicinePanelV2'), {
  ssr: false,
});

type Props = {
  encounterId: string;
  firebaseUidPrefix?: string;
};

export const Telemedicine = React.memo((props: Props) => {
  const { isInitializing, encounter } = useFetchEncounter(props.encounterId);
  const theme = useTheme();

  const { verifyRole } = usePractitionerRole();
  const isVerified = verifyRole('pharmacist');

  const encounterRawId = encounter?.rawId;
  const skywayVersion = encounter?.appointment?.skywayVersion || 'old';

  return (
    <Grid gridTemplateColumns="1fr min-content" gridTemplateRows="1fr" overflow="hidden">
      <Box position="relative" overflow="auto" backgroundColor={theme.colors.background.black}>
        {!isInitializing ? (
          isVerified && encounterRawId ? (
            <>
              {skywayVersion === 'new' ? (
                <TelemedicinePanelV2
                  sessionId={props.encounterId}
                  encounterRawId={encounterRawId}
                  firebaseUidPrefix={props.firebaseUidPrefix}
                  encounter={encounter as unknown as Encounter}
                  isSameDayDelivery={encounter?.appointment.deliveryMethod === 'same_day_delivery'}
                />
              ) : (
                <TelemedicinePanel
                  sessionId={props.encounterId}
                  encounterRawId={encounterRawId}
                  firebaseUidPrefix={props.firebaseUidPrefix}
                  encounter={encounter as unknown as Encounter}
                  isSameDayDelivery={encounter?.appointment.deliveryMethod === 'same_day_delivery'}
                />
              )}
            </>
          ) : (
            <PermissionError />
          )
        ) : null}
      </Box>
      <Box>
        <PatientProfilePanel
          appointment={encounter?.appointment}
          patient={encounter?.appointment.patient}
        />
      </Box>
    </Grid>
  );
});

Telemedicine.displayName = 'Telemedicine';
